import {
  createEntity,
  defaultEntityId,
  ENTITY_PHASE_BODY,
  type EntityInstance,
  integrateBody,
  type Obstacle,
} from '@md/engine';
import type { EntityDef } from '@md/shared';
import { BLOCK_DRAG, BLOCK_SIZE_TILES } from '@md/shared';
import { BlockDefSchema, polaritySign } from './schemas.js';

/**
 * Bloc métallique polarisé (concept §3.2).
 *
 * Un corps solide ordinaire — gravité, collisions AABB avec le décor, les
 * autres objets solides et les robots — dont la seule particularité est de
 * porter une polarité : `flags.magnetic = 1` signale à la règle d'interaction
 * `magnetic-objects` qu'il répond aux champs des robots.
 *
 * Il bouge en phase `BODY`, donc AVANT les boutons : un bloc poussé sur un
 * bouton l'enfonce dans le même tick.
 */
export function createPolarizedBlock(def: EntityDef): EntityInstance {
  const cfg = BlockDefSchema.parse(def);
  const sign = polaritySign(cfg.polarity);
  const margin = (1 - BLOCK_SIZE_TILES) / 2;

  return createEntity({
    id: cfg.id ?? defaultEntityId('block', cfg.x, cfg.y),
    type: 'block',
    x: cfg.x + margin,
    y: cfg.y + margin,
    w: BLOCK_SIZE_TILES,
    h: BLOCK_SIZE_TILES,
    solid: true,
    phase: ENTITY_PHASE_BODY,
    flags: { magnetic: 1, polarity: sign, influenced: 0 },
    appearance: {
      shape: 'box',
      tint: sign > 0 ? 0xd4635f : 0x4b7fd4,
      activeTint: sign > 0 ? 0xff8b7a : 0x76b4ff,
    },
    update(self, ctx) {
      const obstacles: Obstacle[] = [];
      for (const other of ctx.entities) {
        if (other !== self && other.solid) {
          obstacles.push({
            id: other.id,
            box: { x: other.x, y: other.y, w: other.w, h: other.h },
            oneWay: other.oneWay,
          });
        }
      }
      // Les robots arrêtent le bloc : c'est ce qui le fait s'immobiliser
      // juste à côté de celui qui l'attire (l'escabeau du concept §3.2).
      for (const player of ctx.players) {
        if (!player.dead) {
          obstacles.push({
            id: `player-${player.index}`,
            box: {
              x: player.body.x,
              y: player.body.y,
              w: player.body.w,
              h: player.body.h,
            },
            oneWay: false,
          });
        }
      }

      const body = {
        x: self.x,
        y: self.y,
        w: self.w,
        h: self.h,
        vx: self.vx,
        vy: self.vy,
        onGround: false,
      };
      integrateBody(body, ctx.level, { dt: ctx.dt, obstacles });
      self.x = body.x;
      self.y = body.y;
      self.vx = body.vx * (1 - BLOCK_DRAG);
      self.vy = body.vy;
      self.flags.onGround = body.onGround ? 1 : 0;
    },
  });
}
